import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { RoleListRowData } from '../role-list-row/role-list-row';

/**
 * Re-export RoleListRowData as RoleCardData for consistency
 */
export type RoleCardData = RoleListRowData;

/**
 * RoleCard Component
 *
 * Displays a role's information in a card format.
 * Uses the Card component for consistent styling.
 *
 * @example
 * ```html
 * <zag-role-card
 *   [role]="roleData"
 *   [editRoute]="['/settings/roles', roleData.id]"
 *   (cardClick)="onRoleSelected($event)">
 * </zag-role-card>
 * ```
 */
@Component({
  selector: 'zag-role-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, TranslatePipe, ZagIcon],
  host: {
    '[class.zag-role-card]': 'true',
  },
  template: `
    <zag-card variant="interactive" (cardClick)="onCardClick()">
      <!-- Header: Icon, Name and optional Delete button -->
      <div card-header class="zag-role-card__header">
        <span zagIcon="security" class="zag-role-card__icon"></span>
        <div class="zag-role-card__text">
          <h3 class="zag-card-title">{{ role().id }}</h3>
        </div>
        @if (showDelete()) {
          <button
            type="button"
            class="zag-role-card__delete-btn"
            [disabled]="deleteDisabled()"
            [attr.aria-label]="deleteAriaLabel() || null"
            (click)="onDeleteClick($event)"
          >
            <span zagIcon="delete"></span>
          </button>
        }
      </div>

      <!-- Body: Description and Stats -->
      @if (role().description) {
        <p class="zag-role-card__description">{{ role().description }}</p>
      }

      <div class="zag-role-card__stats">
        <span class="zag-role-card__stat">
          <span zagIcon="key"></span>
          {{ role().permissionCount }} {{ 'settings.roles.permissionsCount' | translate }}
        </span>
        <span class="zag-role-card__stat">
          <span zagIcon="person"></span>
          {{ role().userCount }} {{ 'settings.roles.usersCount' | translate }}
        </span>
        <span class="zag-role-card__stat">
          <span zagIcon="groups"></span>
          {{ role().groupCount }} {{ 'settings.roles.groupsCount' | translate }}
        </span>
      </div>
    </zag-card>
  `,
  styles: [
    `
      .zag-role-card {
        display: flex;
        flex: 1;
        align-content: stretch;
        flex-wrap: nowrap;
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
      }

      .zag-role-card .zag-card {
        width: 100%;
      }

      .zag-role-card__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        width: 100%;
      }

      .zag-role-card__icon {
        font-size: 24px;
        color: var(--color-primary);
        flex-shrink: 0;
      }

      .zag-role-card__text {
        flex: 1;
        min-width: 0;
      }

      .zag-role-card__description {
        color: var(--text-color-secondary);
        font-size: 0.875rem;
        margin: 0;
      }

      .zag-role-card__stats {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-md);
        margin-top: var(--spacing-sm);
      }

      .zag-role-card__stat {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);

        [zagIcon] {
          font-size: 16px;
        }
      }

      .zag-role-card__delete-btn {
        margin-left: auto;
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        [zagIcon] {
          font-size: 18px;
        }
      }
    `,
  ],
})
export class RoleCard {
  private readonly router = inject(Router);

  /** The role data to display */
  readonly role = input.required<RoleCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the card is clicked */
  readonly cardClick = output<RoleCardData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<RoleCardData>();

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.role());
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.role());
  }
}
